import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs, useUser } from '../hooks';
import { Button, Field, Groupper, Header, Icon, Input, Message, Modal, Pagination, Table, Toast } from 'react-frontier';
import { bindClick, bindFormChange } from '../SiteUtil';
import { ExpenseType, SetLoading } from '../SiteClasses';
import UserAccess from '../UserAccess';
import API from '../API';
import { Dropdown } from 'semantic-ui-react';

const PAGE_SIZE = 40;
interface ExpenseTypeForm {
	type_name: string;
}

var ExpenseTypesList = ()=>{
	var { hasAccess } = useUser();
	var [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [filter, setFilter] = useState<string>('');
	var [searchExpenseTypes, setSearchExpenseTypes] = useState<ExpenseType[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [loadError, setLoadError] = useState<string>(null);
	
	var [showCreateModal, setShowCreateModal] = useState<boolean>(false);
	var [showEditModal, setShowEditModal] = useState<boolean>(false);
	var [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	var [selectedType, setSelectedType] = useState<ExpenseType | null>(null);
	var [form, setForm] = useState<ExpenseTypeForm>({ type_name: '' });
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		get();
	}, []);

	var get = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		setLoading(true);
		API.getExpenseTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setExpenseTypes(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los tipos de gastos. (GET-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (value: string)=>{
		setFilter(value);
		if(searchTimeout) clearTimeout(searchTimeout);
		
		if(!value.trim()){
			setSearchExpenseTypes(null);
			setSearching(false);
			return;
		}

		setSearching(true);
		var timeout = setTimeout(()=>{
			var filtered = expenseTypes?.filter(type=>
				type.type_name.toLowerCase().includes(value.toLowerCase())
			) || [];
			setSearchExpenseTypes(filtered);
			setSearching(false);
		}, 300);
		setSearchTimeout(timeout);
	};

	var onPageChange = (newPage: number)=>{
		setPage(newPage);
		get(newPage);
	};

	var onCreate = ()=>{
		setForm({ type_name: '' });
		setModalPrompts(null);
		setShowCreateModal(true);
	};

	var onEdit = (expenseType: ExpenseType)=>{
		setSelectedType(expenseType);
		setForm({
			type_name: expenseType.type_name
		});
		setModalPrompts(null);
		setShowEditModal(true);
	};

	var onDelete = (expenseType: ExpenseType)=>{
		setSelectedType(expenseType);
		setModalPrompts(null);
		setShowDeleteModal(true);
	};

	var create = async (setLoading: SetLoading)=>{
		if(!form.type_name.trim()){
			setModalPrompts(['El nombre del tipo es requerido']);
			return;
		}

		setLoading(true);
		API.createExpenseType(form.type_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Tipo de gasto creado exitosamente');
			setExpenseTypes(prev=>[...prev, { type_id: res.data.type_id, type_name: form.type_name.trim(), deleted: false }]);
			setShowCreateModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado creando el tipo de gasto']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var edit = async (setLoading: SetLoading)=>{
		if(!form.type_name.trim()){
			setModalPrompts(['El nombre del tipo es requerido']);
			return;
		}

		setLoading(true);
		API.editExpenseType(selectedType.type_id, form.type_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Tipo de gasto actualizado exitosamente');
			setExpenseTypes(prev=>prev.map(t=>t.type_id === selectedType.type_id ? { ...t, type_name: form.type_name.trim() } : t));
			setShowEditModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado actualizando el tipo de gasto']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var submitDelete = async (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteExpenseType(selectedType.type_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Tipo de gasto eliminado exitosamente');
			setExpenseTypes(prev=>prev.filter(t=>t.type_id !== selectedType.type_id));
			setShowDeleteModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado eliminando el tipo de gasto']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFormChange = bindFormChange(form, setForm);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!expenseTypes) return <Header text='Cargando datos' loading/>

	var shown_types = searchExpenseTypes || expenseTypes;

	return (
		<div>
			<Groupper
				title={'Tipos de gastos'}
				titleRight={hasAccess(UserAccess.ACCOUNTING.EDIT_EXPENSE_TYPE) && (
					<Button iconName='plus' color='black' text='Agregar' size='small' onClick={onCreate}/>
				)}
				style={{ marginTop: 20 }}
				width={600}
			>
				<Input placeholder='Buscar tipo de gasto...' value={filter} onChange={onSearchChange} />
				<Table striped fitted
					className='last top'
					style={{ marginTop: 15 }}
					collapsingIndexes={[0, 2]}
					headers={['ID', 'Nombre', <Icon name='wrench' />]}
					centeredIndexes={[2]}
					footer={(!!shown_types) && (
						<Table.Cell colSpan={3} row>
							<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Pagination page={page} onPageChange={onPageChange} hasNext={shown_types.length>=PAGE_SIZE} />
							</div>
						</Table.Cell>
					)}
				>
					{searching || loading ? (
						<Table.Cell row className='empty' colSpan={3}>
							<Header loading text='Cargando datos' />
						</Table.Cell>
					) : (
						shown_types.length !== 0 ? (
							shown_types.map(type=>(
								<Table.Row
									key={type.type_id}
									collapsingIndexes={[0, 2]}
									data={[
										type.type_id,
										type.type_name,
										<Dropdown key={type.type_id} icon={(
											<div className="fr label" style={{ padding: '3px 10px' }}>
												<Icon name='ellipsis' style={{ margin: 0 }} />
											</div>
										)} className='icon'>
											<Dropdown.Menu>
												{hasAccess(UserAccess.ACCOUNTING.EDIT_EXPENSE_TYPE) && (
													<Dropdown.Item onClick={()=>onEdit(type)}>
														<Icon name="pen" style={{ marginRight: 3 }} /> Editar
													</Dropdown.Item>
												)}
												{hasAccess(UserAccess.ACCOUNTING.EDIT_EXPENSE_TYPE) && (
													<Dropdown.Item onClick={()=>onDelete(type)}>
														<Icon name="trash" style={{ marginRight: 3 }} /> Eliminar
													</Dropdown.Item>
												)}
											</Dropdown.Menu>
										</Dropdown>
									]}
								/>
							))
						) : (
							<Table.Cell row className='empty' colSpan={3}>
								No se encontraron registros
							</Table.Cell>
						)
					)}
				</Table>
			</Groupper>
			<Modal open={showCreateModal} onClose={bindClick(setShowCreateModal, false)} size="tiny">
				<Modal.Header>Crear Tipo de Gasto</Modal.Header>
				<Modal.Content>
					<Field label="Nombre del Tipo">
						<Input
							value={form.type_name}
							onChange={onFormChange('type_name')}
							placeholder="Ej: Transporte, Alimentación, etc."
						/>
					</Field>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowCreateModal, false)} />
					<Button text="Crear" color="black" onClick={create} />
				</Modal.Actions>
			</Modal>
			<Modal open={showEditModal} onClose={bindClick(setShowEditModal, false)} size="tiny">
				<Modal.Header>Editar Tipo de Gasto</Modal.Header>
				<Modal.Content>
					<Input
						label="Nombre del Tipo"
						value={form.type_name}
						onChange={onFormChange('type_name')}
						placeholder="Ej: Transporte, Alimentación, etc."
					/>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowEditModal, false)} />
					<Button text="Guardar" color="black" onClick={edit} />
				</Modal.Actions>
			</Modal>
			<Modal open={showDeleteModal} onClose={bindClick(setShowDeleteModal, false)} size="tiny">
				<Modal.Header>Eliminar Tipo de Gasto</Modal.Header>
				<Modal.Content>
					{selectedType && (
						<div>
							<Header
								text="¿Eliminar tipo de gasto?"
								subtext={`Se eliminará permanentemente el tipo "${selectedType.type_name}"`}
								size="small"
							/>
							<Message type="warning" list={['Esta acción no se puede deshacer']} />
							<Message type="error" list={modalPrompts} />
						</div>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShowDeleteModal, false)} />
					<Button text="Eliminar" color="red" onClick={submitDelete} />
				</Modal.Actions>
			</Modal>
		</div>
	);
};

export default ExpenseTypesList;
